import { MarkerF } from '@react-google-maps/api'
import { DateTime } from 'luxon'
import * as React from 'react'
import TimeAgo from 'react-timeago'
import { IPoint } from '../../../../interfaces/types.ts'
import { OverlayPanel } from '../../../map/components/OverlayPanel.tsx'
import { getNumberCircleIcon, getNumberCircleHighlightedIcon, getNumberPawIcon, getNumberPawHighlightedIcon } from '../../../map/components/icons.ts'
import { getHistoryLabel } from '../../../../helpers/historyHelper.ts'
import { isNoMotionReport } from '../../../../helpers/locationStatusHelper.ts'
import { effectiveTimezone } from '../../../../store/authState.ts'
import { getTextColor } from '../../../../helpers/colorHelper.ts'
import { useSnapshot } from 'valtio'
import { historyState } from '../../../../store/historyState.ts'
import { controlState } from '../../../../store/controlState.ts'

interface Props {
    point: IPoint
    color: string
    num: number
    position: google.maps.LatLng | null
    clusterer?: any
}

export const NumberMarker: React.FC<Props> = (props) => {
    const markerRef = React.useRef(null)
    const [open, setOpen] = React.useState(false)
    const highlight = (useSnapshot(historyState) as typeof historyState).highlight
    const isHighlighted = highlight?._id === props.point._id
    const isMobile = useSnapshot(controlState).isOnMobileDevice

    const handleOnLoad = (markerInstance: any) => {
        markerRef.current = markerInstance
    }

    const tc = getTextColor(props.color)
    const iconColor = tc === '!text-white' ? 'white' : 'black'
    const heading = props.point.properties.heading || 0

    const getIcon = (highlighted: boolean) => {
        if (isMobile) {
            return highlighted
                ? getNumberPawHighlightedIcon(props.color, props.num, iconColor, heading)
                : getNumberPawIcon(props.color, props.num, iconColor, heading)
        }
        return highlighted
            ? getNumberCircleHighlightedIcon(props.color, props.num, iconColor)
            : getNumberCircleIcon(props.color, props.num, iconColor)
    }

    React.useEffect(() => {
        if (markerRef.current) {
            ;(markerRef.current as google.maps.Marker)?.setIcon(getIcon(isHighlighted))
        }
    }, [props.color, isHighlighted])

    if (!props.position) {
        return null
    }

    const petInfo = (
        <div className={'flex flex-col justify-start gap-2 select-none text-primary'}>
            <div className={'flex flex-row justify-center gap-4 align-center'}>
                <div>{DateTime.fromISO(props.point.properties.receivedTime).setZone(effectiveTimezone()?.value).toLocaleString(DateTime.DATETIME_MED_WITH_WEEKDAY)}</div>
                <TimeAgo date={new Date(props.point.properties.receivedTime)}/>
            </div>
            <div className={'flex flex-row justify-around align-center'}>
                <div>Battery {props.point.properties.batteryPercent}</div>
                <div className={`badge badge-sm ${isNoMotionReport(props.point.properties) ? 'badge-accent text-accent-content' : 'badge-outline text-primary opacity-70'}`}>{getHistoryLabel(props.point.properties.gibiMessageType, props.point.properties.status)}</div>
            </div>
        </div>
    )

    const marker = (
        <div>
            {open ?
                <OverlayPanel onClick={() => setOpen(!open)} position={props.position}>
                    {petInfo}
                </OverlayPanel>
                : null}
            <MarkerF
                position={props.position}
                onLoad={handleOnLoad}
                icon={getIcon(isHighlighted)}
                onClick={() => setOpen(!open)}
                clusterer={props.clusterer}
                zIndex={isHighlighted ? 1000 : 1}
            />
        </div>
    )

    return marker
}
